/**
 * Miniature + Titre — « **Le putaclic-o-mètre** » (avenant **A-3** : *doser* · complément
 * **A-3.1** : la physique de l'aiguille). Zéro réflexe de timing : de la lecture et du réglage.
 *
 * Le joueur pousse 2-3 curseurs ; une **seule** aiguille en rend compte, avec inertie,
 * overshoot et tremblement. À T=0 le chrono **fige** l'aiguille où elle est — tremblement
 * compris. Toute la compétence est là : doser tôt, laisser l'inertie s'amortir, et ne pas
 * pousser le putaclic si loin qu'on tombe dans la zone rouge dont on ne voit pas la frontière.
 *
 * ⚠️ SPEC-UI §5.4 (curseur sinusoïdal à arrêter) est **périmée** pour ce jeu.
 *
 * ## Deux contraintes de couture, et elles se répondent
 *
 * 1. **L'aiguille s'intègre à pas FIXE**, celui que le pattern transmet (`pasPhysiqueMs`). À pas
 *    variable, le joueur verrait 94 là où le serveur écrirait 91 — et personne ne saurait qui a
 *    raison. Mieux : la boucle d'affichage lit **la trace**, exactement comme le fera le
 *    serveur. Il n'y a donc plus deux simulations, il n'y en a qu'une.
 * 2. **Les curseurs ont de la course.** Le serveur neutralise l'étape si un curseur bondit plus
 *    vite que `VITESSE_CURSEUR_MAX` : un clic sur la piste d'un `input[type=range]` saute de
 *    100 points en une frame, soit vingt fois trop. La poignée va donc où on la met, mais le
 *    réglage **effectif** l'y rejoint — ce qui est aussi ce que l'avenant décrit : un instrument
 *    indirect, qui répond avec retard.
 *
 * ⟦C57 · lot 8 · A-174⟧ **Moins de texte.** Consigne : une phrase, les nombres, UN avertissement (« ⚠ Trop de
 * putaclic : miniature mensongère »), un badge pour la promesse ; la règle entière, le clavier, la promesse
 * en détail et la leçon de Matt sont derrière « Les dessous ». PENDANT le réglage : le chrono, l'aiguille,
 * les curseurs et un mot (« fige à 0 ») ; jamais une phrase. Bilan : le score, les nombres et un verdict de
 * trois mots ; le verdict d'avant est derrière « Les dessous ».
 */

import { useEffect, useMemo, useRef, useState } from "react";

import {
  ECHANTILLON_VISE_MS,
  MARGE_VITESSE,
  VITESSE_CURSEUR_MAX,
  type TimelineMiniature,
} from "../../api/atelier";
import { DessousBloc, LesDessous } from "../../components/LesDessous";
import { useSfx } from "../../components/SoundProvider";
import { contenu } from "../../game/content";
import { zoneSM } from "../../game/format";
import { useUIStore } from "../../stores/uiStore";
import { BlocChiffresDuTour } from "../core/BlocChiffresDuTour";
import { DessousMiniJeu } from "../core/DessousMiniJeu";
import { ligneExigence } from "../core/exigence";
import { NoteSur100 } from "../core/NoteSur100";
import {
  COMMANDES,
  CONSIGNE,
  MOTS_PUTACLIC,
  VERDICT_PUTACLIC,
  VERDICT_PUTACLIC_LONG,
} from "../core/textes";
import { useChronoSonore } from "../core/useChronoSonore";
import { useRafLoop } from "../core/useRafLoop";
import { useTremblementSM } from "../core/useTremblementSM";
import type { ProprietesMiniJeu } from "../core/types";
import {
  PAS_PHYSIQUE_MS,
  VALEUR_NEUTRE,
  centreCourant,
  cibleAiguille,
  depuisServeur,
  genererReglage,
  noterTraceReglage,
  pasAiguille,
  promesseTenue,
  tremblement,
  zoneApresPromesse,
  centreApresPromesse,
  type PatternPutaclic,
  type ResultatPutaclic,
} from "./reglage";

/** ⟦A-69⟧ « promesse » s'intercale avant « jeu », et seulement si le nœud l'a ouverte. */
type Phase = "pret" | "promesse" | "jeu" | "fige";

/**
 * Période d'échantillonnage des réglages, en ms de jeu. Elle borne mécaniquement la vitesse
 * enregistrée d'un curseur : au pire une course entière par échantillon, soit bien en deçà de
 * ce que le serveur juge humain.
 */
const PERIODE_REGLAGE_MS = 100;

export function PutaclicOMetre({
  stage,
  state,
  seed,
  pattern: servi,
  alignementSujet,
  onFini,
  accueil,
  actionsPlus,
  consequence,
}: ProprietesMiniJeu) {
  const sfx = useSfx();
  /** ⟦A-76⟧ SPEC-UI §7.5 : en zone rouge de SM, la zone de jeu tremble (±1 px / 280 ms) — 
      `secousseSM` et pas `tremblement` : ce nom-là appartient déjà à l'aiguille (reglage.ts). */
  const secousseSM = useTremblementSM(state);
  /** ⟦A-83⟧ `prefers-reduced-motion` : la variante posée (SPEC-UI §7.4), jumelle du serveur. */
  const reducedMotion = useUIStore((s) => s.reducedMotion);
  const pattern = useMemo(() => {
    if (servi) return depuisServeur(servi.patterns.miniature, state);
    // En bac à sable, l'alignement vient du brouillon quand l'écran d'Atelier le connaît ; à
    // défaut, du sujet le plus chaos en main — approximation assumée, jamais un bonus (0 est
    // l'état neutre). Côté compte, le serveur a le vrai `subject_id`.
    const parDefaut = state.inspiration
      .map((c) => contenu().parSujet.get(c.subject_id)?.alignement ?? 0)
      .sort((a, b) => a - b)[0];
    return genererReglage({
      state,
      seed,
      alignementSujet: alignementSujet ?? parDefaut ?? 0,
      pose: reducedMotion,
      zoneBonus: state.skills.includes("miniature_putaclic") ? 1.2 : 1,
      amortisseur: state.skills.includes("plume_d_or"),
      jaugeLisible: state.skills.includes("analytics_addict"),
      zoneRougeSM: zoneSM(state.mental) === "rouge",
      phasePromesse: state.skills.includes("majuscules"),
    });
  }, [servi, state, seed, alignementSujet, reducedMotion]);

  const plan = pattern.promesse;

  const [phase, setPhase] = useState<Phase>("pret");
  // ⟦A-69⟧ La phase d'annonce : son propre chrono, ses propres commandes.
  const [tProm, setTProm] = useState(0);
  const [promCentre, setPromCentre] = useState(() => plan?.departCentre ?? pattern.centre);
  const [promLargeur, setPromLargeur] = useState(() => plan?.departLargeur ?? pattern.zone);
  const [tlPromesse, setTlPromesse] = useState<TimelineMiniature["promesse"]>(undefined);
  const tPromRef = useRef(0);
  const posesPromesse = useRef<NonNullable<TimelineMiniature["promesse"]>["poses"]>([]);
  /** La fourchette effective : celle que le serveur reconstruira, pas l'état d'écran. */
  const fourchette = useMemo(() => promesseTenue(pattern, tlPromesse), [pattern, tlPromesse]);
  /** Ce que le joueur **demande** : c'est la poignée, et elle va où il la met. */
  const [demande, setDemande] = useState<number[]>(() =>
    pattern.curseurs.map(() => VALEUR_NEUTRE),
  );
  const [affichee, setAffichee] = useState(() =>
    cibleAiguille(pattern, pattern.curseurs.map(() => VALEUR_NEUTRE)),
  );
  const [centre, setCentre] = useState(pattern.centre);
  const [restantMs, setRestantMs] = useState(pattern.chronoMs);
  const [resultat, setResultat] = useState<ResultatPutaclic | null>(null);
  const [timeline, setTimeline] = useState<TimelineMiniature | null>(null);

  const demandeRef = useRef(demande);
  demandeRef.current = demande;

  /** Le réglage **effectif** — celui qui est enregistré, donc celui que le serveur relira. */
  const effectif = useRef<number[]>(pattern.curseurs.map(() => VALEUR_NEUTRE));
  const reglages = useRef<TimelineMiniature["reglages"]>([]);
  const prochainEchantillon = useRef(0);

  // La physique : un accumulateur à pas fixe, qui lit la trace. Elle vit dans des refs — elle
  // avance à 120 Hz, elle n'a pas à provoquer un rendu par pas de calcul.
  const position = useRef(cibleAiguille(pattern, pattern.curseurs.map(() => VALEUR_NEUTRE)));
  const vitesse = useRef(0);
  const pasFaits = useRef(0);
  /** Index de lecture de la trace — les instants sont croissants, on ne rescanne pas. */
  const curseurTrace = useRef(0);
  const valeursSimulees = useRef<number[]>(pattern.curseurs.map(() => VALEUR_NEUTRE));

  useRafLoop(phase === "promesse" && plan !== null, (ecoule) => {
    tPromRef.current = ecoule;
    setTProm(ecoule);
    if (plan && ecoule >= plan.chronoMs) finirPromesse();
  });

  /**
   * ⟦A-69⟧ Tracer la fourchette. On enregistre **la commande**, jamais la note : le serveur
   * reborne lui-même et refait le calcul.
   */
  function promettre(centreVise: number, largeurVisee: number) {
    if (phase !== "promesse" || !plan) return;
    const c = Math.max(0, Math.min(100, centreVise));
    const l = Math.max(plan.largeurMin, Math.min(plan.largeurMax, largeurVisee));
    posesPromesse.current = [
      ...posesPromesse.current,
      { atMs: Math.round(tPromRef.current), centre: c, largeur: l },
    ];
    setPromCentre(c);
    setPromLargeur(l);
  }

  function finirPromesse() {
    if (!plan) return;
    const finishedAtMs = Math.min(plan.chronoMs, Math.round(tPromRef.current));
    setTlPromesse({ poses: [...posesPromesse.current], finishedAtMs });
    setPhase("jeu");
  }

  useRafLoop(phase === "jeu", (ecoule) => {
    // 1. Échantillonner le réglage effectif : il rejoint la demande à vitesse bornée.
    while (prochainEchantillon.current <= Math.min(ecoule, pattern.chronoMs)) {
      const t = prochainEchantillon.current;
      const course = VITESSE_CURSEUR_MAX * MARGE_VITESSE * (PERIODE_REGLAGE_MS / 1000);
      const suivant = effectif.current.map((v, i) => {
        const vise = demandeRef.current[i] ?? VALEUR_NEUTRE;
        const delta = Math.max(-course, Math.min(course, vise - v));
        return Math.max(0, Math.min(100, Math.round((v + delta) * 100) / 100));
      });
      const change = suivant.some((v, i) => v !== effectif.current[i]);
      effectif.current = suivant;
      // On n'écrit que ce qui bouge : un réglage inchangé n'apprend rien au serveur, qui tient
      // de toute façon la dernière valeur connue jusqu'au suivant.
      if (change) {
        const atMs = Math.max(
          Math.round(t),
          (reglages.current.at(-1)?.atMs ?? -ECHANTILLON_VISE_MS) + ECHANTILLON_VISE_MS,
        );
        reglages.current.push({ atMs, valeurs: [...suivant] });
      }
      prochainEchantillon.current += PERIODE_REGLAGE_MS;
    }

    // 2. La physique, à pas fixe, lue sur la trace — la simulation du serveur, à l'identique.
    const pas = pattern.pasPhysiqueMs > 0 ? pattern.pasPhysiqueMs : PAS_PHYSIQUE_MS;
    const dt = pas / 1000;
    const limite = Math.min(ecoule, pattern.chronoMs);
    while (pasFaits.current * pas < limite) {
      const t = pasFaits.current * pas;
      while (
        curseurTrace.current < reglages.current.length &&
        reglages.current[curseurTrace.current]!.atMs <= t
      ) {
        valeursSimulees.current = [...reglages.current[curseurTrace.current]!.valeurs];
        curseurTrace.current += 1;
      }
      const cible = cibleAiguille(pattern, valeursSimulees.current);
      const suivant = pasAiguille(pattern, position.current, vitesse.current, cible, dt);
      position.current = Math.max(-20, Math.min(120, suivant.position));
      vitesse.current = suivant.vitesse;
      pasFaits.current += 1;
    }

    const cibleCourante = cibleAiguille(pattern, valeursSimulees.current);
    const c = centreCourant(
      pattern,
      ecoule,
      valeursSimulees.current,
      centreApresPromesse(pattern, fourchette),
    );
    setCentre(c);
    setAffichee(
      Math.max(0, Math.min(100, position.current + tremblement(pattern, ecoule, cibleCourante))),
    );
    setRestantMs(Math.max(0, pattern.chronoMs - ecoule));

    // ⟦A-3.1 point 3⟧ Le chrono FIGE. Pas de validation anticipée, pas de rattrapage — sauf en
    // mode posé (§7.4 : « pas de temps, Entrée pour valider »), où c'est le joueur qui fige.
    if (ecoule >= pattern.chronoMs) figer();
  });

  /**
   * Figer l'aiguille : la timeline soumise s'arrête ici ; la physique, elle, s'intègre jusqu'à
   * `chronoMs` des deux côtés (client et serveur rejouent la même trace à pas fixe), donc figer
   * tôt ne change pas la note — c'est ce qui rend « Entrée pour valider » honnête.
   * Copie, pour la même raison qu'au Tournage : un instantané, pas une vue vivante.
   */
  function figer() {
    const jouee: TimelineMiniature = {
      reglages: [...reglages.current],
      ...(tlPromesse ? { promesse: tlPromesse } : {}),
    };
    setTimeline(jouee);
    setResultat(noterTraceReglage(pattern, jouee.reglages, tlPromesse));
    sfx("cursor_stop");
    setPhase("fige");
  }

  // §7.4 posé : Entrée valide.
  useEffect(() => {
    if (phase !== "jeu" || !pattern.pose) return;
    const auClavier = (e: KeyboardEvent) => {
      if (e.key !== "Enter") return;
      e.preventDefault();
      figer();
    };
    window.addEventListener("keydown", auClavier);
    return () => window.removeEventListener("keydown", auClavier);
    // `figer` lit des refs : en dépendre le rendrait instable sans rien changer au geste.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, pattern.pose]);

  // Les cinq dernières secondes se font entendre (§7.6) — pas en mode posé : pas de temps.
  useChronoSonore(restantMs, phase === "jeu" && !pattern.pose);

  useEffect(() => {
    if (phase !== "jeu") return;
    prochainEchantillon.current = 0;
    pasFaits.current = 0;
  }, [phase]);

  if (phase === "pret") {
    return (
      <div className="jeu jeu-putaclic">
        <h3 className="jeu-titre">🖼️ Le putaclic-o-mètre</h3>
        {/* ⟦C62 · friction 3⟧ La 1re fois, la réplique de Matt tient la place de la phrase de règle : un seul écran. */}
        {accueil ?? <p className="jeu-regle">{CONSIGNE.miniature.regle}</p>}
        <p className="jeu-regle jeu-commandes">{COMMANDES.miniature}</p>
        <ul className="jeu-params">
          <li>
            <strong>{(pattern.chronoMs / 1000).toFixed(0)}</strong> s
          </li>
          {/* ⟦C62 · friction 7⟧ « zone 6,0 pts » et « exigence E0 » sont derrière « Les dessous » (« Les chiffres
              du tour ») ; « la zone bouge » reste : c'est une règle du tour, pas un réglage. */}
          {pattern.drift > 0 ? <li>la zone bouge</li> : null}
        </ul>
        <p className="jeu-seuils">{CONSIGNE.miniature.mensonge}</p>
        {plan ? (
          <p className="jeu-variante" role="note">
            <span className="jeu-variante-badge">🔠 MAJUSCULES</span> {CONSIGNE.miniature.promesse}
          </p>
        ) : null}
        <div className="jeu-actions">
          {/* ⟦C62 · friction 3⟧ Un seul libellé pour lancer un mini-jeu : « Jouer ». */}
          <button
            className="bouton primaire grand"
            type="button"
            onClick={() => setPhase(plan ? "promesse" : "jeu")}
          >
            Jouer
          </button>
          {actionsPlus}
          {/* ⟦C57 · lot 8⟧ Les phrases d'avant, au mot près : la règle, le clavier, la limite du mensonge, la
              promesse en détail (la phase d'annonce est chronométrée : rien à lire dedans), puis la leçon de Matt. */}
          <DessousMiniJeu stage={stage}>
            <BlocChiffresDuTour
              lignes={[
                `Zone : ${pattern.zone.toFixed(1).replace(".", ",")} points`,
                ligneExigence(pattern.k),
              ]}
            />
            <DessousBloc titre="La règle">
              <p className="dessous-texte">
                {pattern.curseurs.length} curseurs, une seule aiguille, et elle a du retard. Trouve
                lequel fait quoi, amène-la dans la zone, et <strong>stabilise-la avant la fin</strong> :
                à zéro, le chrono la fige là où elle est.
              </p>
              <p className="dessous-texte">
                Les curseurs se règlent aussi au clavier (Tab, puis ← →). L'aiguille répond avec du
                retard : anticipe.
              </p>
              <p className="dessous-texte">
                Trop de putaclic et la miniature devient mensongère. Tu ne verras pas la limite.
              </p>
            </DessousBloc>
            {plan ? (
              <DessousBloc titre="MAJUSCULES : la promesse">
                <p className="dessous-texte">
                  🔠 <strong>MAJUSCULES</strong> : avant de régler quoi que ce soit, tu annonces ce que
                  la miniature promet. Ta fourchette <em>devient</em> la zone à tenir : c'est toi qui
                  places la cible, et c'est toi qui devras l'atteindre.
                </p>
                <p className="dessous-texte">
                  Annonce ce que ta miniature promet. <strong>Ta fourchette devient la zone à tenir</strong>
                  , et plus tu promets gros, plus l'aiguille tremblera quand il faudra l'y amener.
                </p>
                <p className="dessous-texte">
                  Une fourchette serrée rapporte plein pot et se rate. Une fourchette large est un
                  filet, et se paie. La bande rouge, c'est le mensonge : on peut viser dedans, c'est le
                  tremblement qui décidera.
                </p>
              </DessousBloc>
            ) : null}
          </DessousMiniJeu>
        </div>
      </div>
    );
  }

  // ⟦A-69⟧ La phase « La promesse » — on **annonce**, on ne règle rien encore.
  if (phase === "promesse" && plan) {
    const bas = Math.max(0, promCentre - promLargeur / 2);
    const haut = Math.min(100, promCentre + promLargeur / 2);
    return (
      <div className="jeu jeu-putaclic jeu-promesse" style={secousseSM}>
        <h3 className="jeu-titre">🔠 La promesse</h3>
        <p className="jeu-regle">{CONSIGNE.miniature.promesse}</p>
        <Vumetre
          pattern={pattern}
          aiguille={promCentre}
          centre={promCentre}
          largeur={promLargeur}
          montrerFrontiere
        />
        <div className="montage-entete">
          <span className="montage-compteur">
            je promets <strong>{Math.round(promCentre)}</strong> · fourchette{" "}
            <strong>{bas.toFixed(0)}–{haut.toFixed(0)}</strong>
          </span>
          <span className="montage-restant">
            {((plan.chronoMs - tProm) / 1000).toFixed(1)} s
          </span>
        </div>

        <div className="curseurs">
          <label className="curseur">
            <span className="curseur-bout">sobre</span>
            <input
              type="range"
              min={0}
              max={100}
              step={1}
              value={Math.round(promCentre)}
              aria-label="Niveau de putaclic promis"
              onChange={(e) => promettre(Number(e.target.value), promLargeur)}
            />
            <span className="curseur-bout">énorme</span>
          </label>
          <label className="curseur">
            <span className="curseur-bout">serrée</span>
            <input
              type="range"
              min={plan.largeurMin}
              max={plan.largeurMax}
              step={0.1}
              value={promLargeur}
              aria-label="Largeur de la fourchette promise"
              onChange={(e) => promettre(promCentre, Number(e.target.value))}
            />
            <span className="curseur-bout">large</span>
          </label>
        </div>

        <p className="jeu-seuils">{MOTS_PUTACLIC.frontiere}</p>
        <button className="bouton primaire grand" type="button" onClick={finirPromesse}>
          Suivant
        </button>
      </div>
    );
  }

  if (phase === "fige" && resultat && timeline) {
    const verdictCle: keyof typeof VERDICT_PUTACLIC = resultat.mensonger
      ? "mensonger"
      : resultat.dansLaZone
        ? "dansLaZone"
        : resultat.aiguille < resultat.centre
          ? "trop_sage"
          : "trop_gros";
    return (
      <div className="jeu jeu-putaclic">
        <h3 className="jeu-titre">🖼️ Figé.</h3>
        <Vumetre
          pattern={pattern}
          aiguille={resultat.aiguille}
          centre={resultat.centre}
          largeur={zoneApresPromesse(pattern, fourchette)}
          fige
          mensonger={resultat.mensonger}
        />
        <NoteSur100 note={resultat.score} consequence={consequence?.(resultat.score)} />
        {resultat.promesse !== null && resultat.dosage !== null ? (
          <ul className="jeu-params">
            <li>
              dosage <strong>{Math.round(resultat.dosage)}</strong> · promesse{" "}
              <strong>{Math.round(resultat.promesse.score)}</strong>
            </li>
            <li>
              tu promettais <strong>{Math.round((fourchette?.centre ?? 0))}</strong>, à{" "}
              <strong>±{((fourchette?.largeur ?? 0) / 2).toFixed(1).replace(".", ",")}</strong>
            </li>
          </ul>
        ) : null}
        <p className="jeu-seuils">{VERDICT_PUTACLIC[verdictCle]}</p>
        <div className="jeu-actions">
          <button
            className="bouton primaire grand"
            type="button"
            onClick={() => onFini(resultat.score, timeline)}
          >
            Suivant
          </button>
          {/* ⟦C57 · lot 8⟧ Le verdict d'avant, mot pour mot. */}
          <LesDessous titre="Le verdict du putaclic-o-mètre">
            <DessousBloc>
              <p className="dessous-texte">{VERDICT_PUTACLIC_LONG[verdictCle]}</p>
            </DessousBloc>
          </LesDessous>
        </div>
      </div>
    );
  }

  return (
    <div className="jeu jeu-putaclic" style={secousseSM}>
      <div className="montage-entete">
        <span className="montage-compteur">
          {pattern.pose ? MOTS_PUTACLIC.figePose : MOTS_PUTACLIC.fige}
        </span>
        {pattern.pose ? (
          <button type="button" className="bouton" onClick={figer}>
            {MOTS_PUTACLIC.valider}
          </button>
        ) : (
          <span className="montage-restant">{(restantMs / 1000).toFixed(1)} s</span>
        )}
      </div>

      <Vumetre
        pattern={pattern}
        aiguille={affichee}
        centre={centre}
        largeur={zoneApresPromesse(pattern, fourchette)}
      />

      <div className="curseurs">
        {pattern.curseurs.map((curseur, i) => (
          <label className="curseur" key={curseur.id}>
            <span className="curseur-bout">{curseur.gauche}</span>
            <input
              type="range"
              min={0}
              max={100}
              // §7.4 posé : « flèches ← → (2 pts par appui) ».
              step={pattern.pose ? 2 : 1}
              value={demande[i] ?? VALEUR_NEUTRE}
              aria-label={`${curseur.gauche} vers ${curseur.droite}`}
              onChange={(e) => {
                const suivant = [...demande];
                suivant[i] = Number(e.target.value);
                setDemande(suivant);
              }}
            />
            <span className="curseur-bout">{curseur.droite}</span>
          </label>
        ))}
      </div>

    </div>
  );
}

/** Le vumètre : un demi-cadran tracé au marqueur, avec sa zone et son aiguille. */
function Vumetre({
  pattern,
  aiguille,
  centre,
  fige = false,
  mensonger = false,
  largeur,
  montrerFrontiere = false,
}: {
  pattern: PatternPutaclic;
  aiguille: number;
  centre: number;
  fige?: boolean;
  mensonger?: boolean;
  /** ⟦A-69⟧ La largeur de la fourchette promise. À défaut, la zone tirée. */
  largeur?: number;
  /**
   * ⟦A-69⟧ Pendant la phase d'annonce, la frontière rouge est **dessinée** : c'est ce qui rend
   * la décision informée au lieu d'être une loterie. La pénalité, elle, porte sur où l'aiguille
   * FINIT — voir `noterPromesse` : sinon il suffirait de promettre un point en dessous.
   */
  montrerFrontiere?: boolean;
}) {
  const CX = 150;
  const CY = 148;
  const R = 118;
  const point = (valeur: number, rayon: number) => {
    const rad = (Math.PI * (100 - valeur)) / 100;
    return [CX + rayon * Math.cos(rad), CY - rayon * Math.sin(rad)] as const;
  };
  const arc = (de: number, a: number, rayon: number) => {
    const [x1, y1] = point(de, rayon);
    const [x2, y2] = point(a, rayon);
    return `M${x1.toFixed(1)},${y1.toFixed(1)} A${rayon},${rayon} 0 0 1 ${x2.toFixed(1)},${y2.toFixed(1)}`;
  };

  const large = largeur ?? pattern.zone;
  const bas = Math.max(0, centre - large / 2);
  const haut = Math.min(100, centre + large / 2);
  const [ax, ay] = point(Math.max(0, Math.min(100, aiguille)), R - 16);

  return (
    <svg className={`vumetre ${fige ? "fige" : ""}`} viewBox="0 0 300 168" role="img"
      aria-label={`Aiguille à ${Math.round(aiguille)} sur 100, zone visée de ${Math.round(bas)} à ${Math.round(haut)}`}>
      <path d={arc(0, 100, R)} className="vu-cadran" />
      <path d={arc(bas, haut, R)} className="vu-zone" />
      {/* La zone rouge « mensongère » ne se montre QU'UNE FOIS franchie (A-3.1 point 4). */}
      {mensonger || montrerFrontiere ? (
        <path d={arc(pattern.seuilMensonge, 100, R)} className="vu-mensonge" />
      ) : null}
      <line x1={CX} y1={CY} x2={ax} y2={ay} className="vu-aiguille" />
      <circle cx={CX} cy={CY} r={7} className="vu-axe" />
      <text x={22} y={162} className="vu-legende">
        fade
      </text>
      <text x={232} y={162} className="vu-legende">
        putaclic
      </text>
    </svg>
  );
}
